/**
 * PrivacyBadge Component
 *
 * Tappable lock icon badge showing encryption status in the MessageInput toolbar.
 * Opens PrivacySummaryModal on tap with details about privacy features.
 *
 * Design: Follows CrisisBanner patterns (Pressable, testID, i18n, analytics)
 * Accessibility: 44px min touch target, screen reader labels
 *
 * @see DD-376 — Privacy Badge & Policy Copy
 */

import { Pressable, StyleSheet } from 'react-native';
import { useState } from 'react';
import Ionicons from '@expo/vector-icons/Ionicons';
import { useTranslation } from '@dramadetector/shared';
import { captureEvent } from '../../lib/analytics';
import { PrivacySummaryModal } from './PrivacySummaryModal';

export function PrivacyBadge(): React.JSX.Element {
  const { t } = useTranslation('home');
  const [modalVisible, setModalVisible] = useState(false);

  const handlePress = (): void => {
    captureEvent('Privacy_Badge_Tapped', { screen: 'home' });
    setModalVisible(true);
  };

  const handleModalClose = (): void => {
    setModalVisible(false);
  };

  return (
    <>
      <Pressable
        onPress={handlePress}
        style={styles.badge}
        testID="privacy-badge"
        accessibilityRole="button"
        accessibilityLabel={t('privacy_badge.label')}
        accessibilityHint={t('privacy_badge.accessibility_hint')}
      >
        <Ionicons name="lock-closed" size={12} color="#10b981" />
      </Pressable>

      <PrivacySummaryModal
        visible={modalVisible}
        onClose={handleModalClose}
      />
    </>
  );
}

const styles = StyleSheet.create({
  badge: {
    alignItems: 'center',
    paddingHorizontal: 8,
    paddingVertical: 4,
    minHeight: 44,
    justifyContent: 'center',
  },
});
